/**
 * syncFilesystem — the DRY core of the desktop filesystem connector's sync
 * path, shared by BOTH the connect dialog (one-shot sync on connect) and the
 * ``useFilesystemAutoSync`` hook (live watcher-driven incremental sync).
 *
 * Everything here is desktop-only and goes through ``fsBridge`` — NEVER
 * ``@tauri-apps/*`` directly. The token + silent-refresh story is single-sourced
 * in the desktop token store, mirroring ``useDesktopOAuth.doRefresh``.
 *
 * Two reusable operations:
 *   - ``runFilesystemSync`` — walk + upload changed files (skip-unchanged makes
 *     a full-root rescan cheap), tracking every cloud job in the sync store,
 *     with a single transparent refresh-and-retry on 401.
 *   - ``runFilesystemDelete`` — drop a removed path from the cloud index.
 *
 * The scan profile constants (extensions / excludes / size cap) live here so
 * the dialog preview, the dialog confirm, and the auto-sync hook can't drift.
 */

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { type DesktopTokensResponse, getEnv, refreshDesktopTokens } from "@ando/core";
import { apiClient } from "@ando/core/api";

import {
  readAccessToken,
  readRefreshToken,
  saveDesktopTokens,
} from "@/features/auth/desktop/tokenStore";
import { useSyncStore } from "@/features/sync/state/syncStore";

import {
  type SmbConfig,
  type SyncConfig,
  type SyncSummary,
  fsDeleteFile,
  fsPathExists,
  fsSync,
  fsSyncSmb,
  fsTryReadDir,
  onSyncProgress,
} from "./fsBridge";
import { readSmbCredentials } from "./smbCredentialStore";

// ---------------------------------------------------------------------------
// Scan defaults (local-folder v1) — sensible doc-only profile.
// ---------------------------------------------------------------------------

/** Lower-cased extensions WITHOUT the dot. The doc-only ingest profile.
 *
 * CAD extensions (DXF/DWG/STEP/STP/IGES/IGS/STL) — June 2026 parity.
 * The BE routes them to the 3D analyzer (``step_io_service`` /
 * ``step_analysis_service``) which generates a PBR preview + drawing
 * SVG + geometric summary. The Tecnico hub library shows them with the
 * format-specific badge.
 */
export const FS_DEFAULT_EXTENSIONS: readonly string[] = [
  // OFFLINE FALLBACK ONLY (Aug 26, 2026) — the LIVE whitelist is served by
  // the BE in the capabilities manifest (``ingest.filesystem_extensions``,
  // derived from the single canonical ``mime_types.SUPPORTED_EXTENSIONS``)
  // and resolved via ``getScanExtensions()`` below. This static list exists
  // solely for BE-unreachable / older-BE cases; keep it a mirror of the BE
  // source MINUS code extensions (deliberate: a folder containing a repo
  // would index thousands of source files — code connector in standby).
  "pdf",
  "docx",
  "doc",
  "pptx",
  "ppt",
  "xlsx",
  "xls",
  "txt",
  "md",
  "html",
  "csv",
  // Images — ingested via vision captions into RAG.
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "tiff",
  "bmp",
  // 2D / 3D CAD (mechanical)
  "dxf",
  "dwg",
  "step",
  "stp",
  "iges",
  "igs",
  "stl",
  // 3D garment meshes + fashion project files (Fashion CAD axis)
  "obj",
  "gltf",
  "glb",
  "fbx",
  "zprj",
  "zpac",
];

// Session-scoped cache of the BE-served whitelist: ONE manifest fetch per
// process, every sync/connect reuses it. ``null`` sentinel = fetch failed →
// retry on the next call (a transient network blip must not freeze the
// fallback list for the whole session).
let _scanExtensionsCache: readonly string[] | null = null;

/**
 * The filesystem-scan extension whitelist — SINGLE SOURCE OF TRUTH resolution
 * (Aug 26, 2026). Order: BE capabilities manifest (``ingest.
 * filesystem_extensions``, derived server-side from
 * ``mime_types.SUPPORTED_EXTENSIONS`` — canonical) → static
 * ``FS_DEFAULT_EXTENSIONS`` fallback (BE unreachable / older BE build).
 * Never throws.
 */
export async function getScanExtensions(): Promise<readonly string[]> {
  if (_scanExtensionsCache) return _scanExtensionsCache;
  try {
    const { fetchCapabilityManifest } = await import("@ando/core");
    const manifest = await fetchCapabilityManifest();
    const served = manifest.ingest?.filesystem_extensions;
    if (Array.isArray(served) && served.length > 0) {
      _scanExtensionsCache = served;
      return served;
    }
  } catch {
    // BE unreachable — fall through to the static mirror.
  }
  return FS_DEFAULT_EXTENSIONS;
}

/**
 * Refine a ZERO-MATCH scan verdict (Aug 26, 2026). The Rust walker reports an
 * empty scan BOTH when the folder truly has no compatible files AND when
 * macOS TCC denies the app access to a gated location (Downloads / Documents
 * / Desktop) — observed live: an existing folder with a valid ``.docx``
 * still toasted "no compatible files". Probe each root with a shallow
 * plugin-fs readDir (same process → same TCC verdict as the scanner):
 *   - any root unreadable            → "permission_denied"
 *   - readable but no entries at all → "empty"
 *   - readable with entries          → "no_compatible" (the honest old case)
 */
export async function diagnoseEmptyScan(
  roots: readonly string[],
): Promise<"permission_denied" | "empty" | "no_compatible"> {
  let sawEntries = false;
  for (const r of roots) {
    const entries = await fsTryReadDir(r);
    if (entries === null) return "permission_denied";
    if (entries.length > 0) sawEntries = true;
  }
  return sawEntries ? "no_compatible" : "empty";
}

/** The System-Settings walkthrough for a TCC denial — ONE string reused by
 * every surface that can hit it (manage-sync toast + connect-dialog banner). */
export const FS_PERMISSION_MESSAGE = msg`macOS sta bloccando l'accesso di Ando a questa cartella. Apri Impostazioni di Sistema → Privacy e Sicurezza → File e Cartelle → Ando e abilita la cartella (o sposta la cartella fuori da Download/Documenti/Scrivania).`;

/** Glob excludes — Office lock files, temp files, VCS + dependency trees. */
export const FS_DEFAULT_EXCLUDES: readonly string[] = [
  "~$*",
  "*.tmp",
  ".git/**",
  "node_modules/**",
];

/** Skip files larger than this — keeps ingest cheap + bounded. */
export const FS_MAX_FILE_SIZE_MB = 100;

/** Never traverse symlinks — avoids cycles + escaping the chosen root. */
export const FS_FOLLOW_SYMLINKS = false;

// ---------------------------------------------------------------------------
// Silent desktop refresh
// ---------------------------------------------------------------------------

/**
 * Silent desktop refresh — mirrors ``useDesktopOAuth.doRefresh``. Rotates the
 * access token, write-through to the OS keychain (Rust `keyring`), and returns the fresh access
 * token (or ``null`` when no refresh token / the IdP call fails).
 */
export async function refreshDesktopAccessToken(): Promise<string | null> {
  const refreshToken = await readRefreshToken();
  if (!refreshToken) return null;
  try {
    const resp: DesktopTokensResponse = await refreshDesktopTokens({
      refresh_token: refreshToken,
    });
    await saveDesktopTokens({
      access_token: resp.access_token,
      refresh_token: resp.refresh_token,
      id_token: resp.id_token,
      expires_at: Date.now() + resp.expires_in * 1000,
      user_info: resp.user_info,
    });
    return resp.access_token;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export interface RunFilesystemSyncArgs {
  /** Cloud connector id to sync against. */
  readonly connectorId: string;
  /** Absolute roots to walk + upload from. */
  readonly roots: readonly string[];
  /** Connector-level ACL allow-list (emails). */
  readonly allowedUsers?: readonly string[];
  /** Live per-file progress — receives the cumulative uploaded count. */
  readonly onProgress?: (uploaded: number) => void;
}

/** The error surfaced when there's no desktop session to authenticate with. */
const NO_SESSION_MESSAGE = msg`Sessione desktop assente. Riconnetti l'app.`;
/** The error surfaced when the refresh fails / a 401 persists after retry. */
const EXPIRED_SESSION_MESSAGE = msg`Sessione desktop scaduta. Riconnetti l'app.`;

/** The error surfaced when an SMB connector has lost its stored credentials. */
const SMB_CREDS_MISSING_MESSAGE = msg`Credenziali della cartella di rete mancanti. Riconnetti la cartella.`;

/**
 * The DRY core of every filesystem sync. Reads the desktop token, builds the
 * ``SyncConfig``, wires best-effort per-file progress, runs ``sync`` (the local
 * or SMB transport injected by the caller), transparently refreshes-and-retries
 * ONCE on a 401, and tracks every returned cloud job. Local + SMB differ ONLY in
 * the injected ``sync`` closure, so the auth/retry/progress/tracking story
 * can't drift between them.
 */
async function runSyncCore(params: {
  readonly connectorId: string;
  readonly roots: readonly string[];
  readonly allowedUsers?: readonly string[];
  readonly onProgress?: (uploaded: number) => void;
  readonly sync: (config: SyncConfig) => Promise<SyncSummary>;
}): Promise<SyncSummary> {
  const { connectorId, roots, allowedUsers, onProgress, sync } = params;

  let token = await readAccessToken();
  if (!token) throw new Error(i18n._(NO_SESSION_MESSAGE));

  // BE-served whitelist (single source: mime_types.py) with static fallback.
  const scanExtensions = await getScanExtensions();

  const apiBase = getEnv().apiUrl;
  const users = [...(allowedUsers ?? [])];
  const buildConfig = (bearer: string): SyncConfig => ({
    scan: {
      roots: [...roots],
      extensions: [...scanExtensions],
      excludeGlobs: [...FS_DEFAULT_EXCLUDES],
      maxFileSizeMb: FS_MAX_FILE_SIZE_MB,
      followSymlinks: FS_FOLLOW_SYMLINKS,
      connectorId,
    },
    apiBase,
    token: bearer,
    allowedUsers: users,
  });

  // Live per-file progress — best-effort; unlisten in finally.
  let unlistenProgress: (() => void) | undefined;
  if (onProgress) {
    try {
      unlistenProgress = await onSyncProgress((p) => onProgress(p.uploadedFiles));
    } catch {
      // not in Tauri / no listener — progress is optional.
    }
  }

  try {
    let summary = await sync(buildConfig(token));

    if (summary.authExpired) {
      const fresh = await refreshDesktopAccessToken();
      if (!fresh) throw new Error(i18n._(EXPIRED_SESSION_MESSAGE));
      token = fresh;
      summary = await sync(buildConfig(token));
      if (summary.authExpired) throw new Error(i18n._(EXPIRED_SESSION_MESSAGE));
    }

    const { trackJob } = useSyncStore.getState();
    for (const jobId of summary.jobIds) {
      trackJob(jobId, "filesystem", { connector_id: connectorId });
    }

    // A healthy incremental scan often uploads nothing. Report the terminal
    // scan separately from per-file ingest so the cloud timestamp reflects
    // the last successful check instead of the last changed file.
    await apiClient.post("/filesystem/desktop/sync-complete", {
      connector_id: connectorId,
      matched_files: summary.matchedFiles,
      uploaded_files: summary.uploadedFiles,
      failed_files: summary.failedFiles,
    });

    return summary;
  } finally {
    if (unlistenProgress) unlistenProgress();
  }
}

/**
 * Walk ``roots`` (LOCAL OS paths) + upload changed files through the cloud
 * ingest contract. Thin wrapper over ``runSyncCore`` with the local transport.
 *
 * Used by the connect dialog (one-shot on connect) AND the auto-sync hook
 * (per watcher batch) — the single sync code path so they can't drift.
 */
export async function runFilesystemSync(args: RunFilesystemSyncArgs): Promise<SyncSummary> {
  // MISSING-ROOT GUARD (Aug 26, 2026): scanning a root that no longer exists
  // silently yields ``matchedFiles: 0`` and the UI told the user "no
  // compatible files" — observed live with folders MOVED to an external SSD
  // (connector still pointing at the old ~/Documents path). Say what actually
  // happened, with the unmounted-volume case called out separately. LOCAL
  // transport only — SMB roots are share-relative, not OS paths.
  const missing: string[] = [];
  for (const r of args.roots) {
    if (!(await fsPathExists(r))) missing.push(r);
  }
  if (missing.length > 0 && missing.length === args.roots.length) {
    const first = missing[0] ?? "";
    throw new Error(
      first.startsWith("/Volumes/")
        ? i18n._(
            msg`Disco esterno non montato (${first}). Collega il disco e riprova.`,
          )
        : i18n._(
            msg`Cartella non trovata: ${first} — è stata spostata o eliminata. Rimuovi la cartella e ricollegala dal percorso attuale.`,
          ),
    );
  }
  if (missing.length > 0) {
    console.warn("[fs-sync] roots mancanti (scansiono i rimanenti):", missing);
  }
  return runSyncCore({ ...args, sync: (config) => fsSync(config) });
}

/** Args for ``runFilesystemSmbSync`` — a local sync plus the share credentials.
 * ``roots`` here are SHARE-RELATIVE (``""`` ⇒ the whole share), not OS paths. */
export interface RunFilesystemSmbSyncArgs extends RunFilesystemSyncArgs {
  /** Programmatic-SMB connection credentials (from the OS keychain store, Rust `keyring`). */
  readonly smb: SmbConfig;
}

/**
 * Walk a network share PROGRAMMATICALLY over SMB (no OS mount) + upload changed
 * files. Thin wrapper over ``runSyncCore`` with the SMB transport — same auth,
 * retry, progress, and job-tracking as the local path.
 */
export async function runFilesystemSmbSync(args: RunFilesystemSmbSyncArgs): Promise<SyncSummary> {
  const { smb, ...rest } = args;
  return runSyncCore({ ...rest, sync: (config) => fsSyncSmb(config, smb) });
}

/** True when a connector's roots are programmatic-SMB ``smb://…`` logical paths
 * (the connector ``base_paths`` marker) rather than local OS paths. */
export function isSmbConnector(roots: readonly string[]): boolean {
  return roots.length > 0 && roots.every((r) => r.startsWith("smb://"));
}

/**
 * Route a connector to the correct sync transport: programmatic SMB (credentials
 * + share-relative roots from the OS keychain store) or the local OS path. The
 * SINGLE dispatcher shared by the wizard, the live auto-sync hook, and the
 * nightly tick so the three can't drift. ``roots`` is the connector's
 * ``base_paths`` (used only to detect SMB — the actual SMB scan roots come from
 * the stored record).
 */
export async function runConnectorSync(args: RunFilesystemSyncArgs): Promise<SyncSummary> {
  if (isSmbConnector(args.roots)) {
    const record = await readSmbCredentials(args.connectorId);
    if (!record) throw new Error(i18n._(SMB_CREDS_MISSING_MESSAGE));
    return runFilesystemSmbSync({
      connectorId: args.connectorId,
      smb: record,
      roots: record.roots.length > 0 ? record.roots : [""],
      allowedUsers: args.allowedUsers,
      onProgress: args.onProgress,
    });
  }
  return runFilesystemSync(args);
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

/**
 * Drop a removed path from the cloud index (watcher deletion path). Best-effort
 * on auth: reads the access token, refreshes once if absent, and skips silently
 * when there's still no session — a delete we can't authenticate is dropped
 * rather than throwing, since the watcher fires these unattended.
 */
export async function runFilesystemDelete(connectorId: string, filePath: string): Promise<void> {
  let token = await readAccessToken();
  if (!token) {
    const fresh = await refreshDesktopAccessToken();
    if (!fresh) return; // no session — skip silently.
    token = fresh;
  }
  await fsDeleteFile({
    connectorId,
    filePath,
    apiBase: getEnv().apiUrl,
    token,
  });
}
